import {
  ActionSheetItem,
  Avatar,
  AvatarSize,
  DropListButton,
  ListItemText,
  theme,
  TabSegmented,
  TabSegmentedGroup,
} from '@ketch-com/deck'
import { Box, Typography } from '@mui/material'
import { SearchTextInput } from 'components/searchTextInput/SearchTextInput'
import { useDataSentryContext } from '../context/DataSentryContext'
import { DataSystemDetails } from '@ketch-com/windlass/dist/dataSentry/v1/systems/systems'
import {
  AuthorizationStatus as TrackerAuthorizationStatus,
  ControlStatus as TrackerControlStatus,
} from '@ketch-com/windlass/dist/dataSentry/v1/trackers/trackers'
import { getSystemName } from 'pages/dataSentry/utils/getSystemName'

export interface DataSentryTableFiltersProps {
  searchQuery: string
  handleSearchInputChange: (nextValue: string) => void
  dataSystemFilterValue?: DataSystemDetails
  setDataSystemFilterValue?: (value: DataSystemDetails | undefined) => void
  trackerAuthorizationStatus?: TrackerAuthorizationStatus
  setTrackerAuthorizationStatus?: (value: TrackerAuthorizationStatus) => void
  trackerControlStatus?: TrackerControlStatus
  setTrackerControlStatus?: (value: TrackerControlStatus) => void
  setPaginationModel?: React.Dispatch<
    React.SetStateAction<{
      page: number
      pageSize: number
    }>
  >
}

export const DataSentryTableFilters: React.FC<DataSentryTableFiltersProps> = ({
  searchQuery,
  handleSearchInputChange,
  dataSystemFilterValue,
  setDataSystemFilterValue,
  trackerAuthorizationStatus,
  setTrackerAuthorizationStatus,
  trackerControlStatus,
  setTrackerControlStatus,
  setPaginationModel,
}) => {
  const { filteredDataSystemListOptions, isLoadingDataSystemListOptions: isLoading } = useDataSentryContext()

  return (
    <Box display="flex" justifyContent="space-between">
      <Box display="flex" gap={2}>
        <SearchTextInput
          onChange={nextValue => handleSearchInputChange(nextValue)}
          value={searchQuery}
          showClearButton
          minWidth="176px"
          sx={{
            width: '176px',
            '& .MuiInputBase-input': { maxWidth: '112px', minWidth: '112px' },
          }}
        />
        {dataSystemFilterValue ? (
          <DropListButton
            onChange={(_, value) => setDataSystemFilterValue?.(value || undefined)}
            size="small"
            placeholder="All Systems"
            getOptionLabel={option => getSystemName(option)}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            options={filteredDataSystemListOptions}
            disabled={isLoading}
            value={dataSystemFilterValue}
            renderOption={(props, option, { selected }) => (
              <ActionSheetItem selected={selected} {...props}>
                <ListItemText selected={selected}>
                  {
                    <Box display="flex" alignItems="center" gap={0.5} width="100%">
                      {option.logoUrl ? (
                        <Avatar src={option.logoUrl} size={AvatarSize.small} />
                      ) : (
                        <Avatar icon="FUnknown" size={AvatarSize.small} textColor={theme.palette.Black.o48} />
                      )}
                      {option.name || option.code}
                    </Box>
                  }
                </ListItemText>
              </ActionSheetItem>
            )}
          />
        ) : null}
      </Box>
      {trackerAuthorizationStatus ? (
        <Box display="flex" alignItems="center" gap={1}>
          {trackerAuthorizationStatus === TrackerAuthorizationStatus.AUTHORIZED && trackerControlStatus ? (
            <TabSegmentedGroup
              size="small"
              value={trackerControlStatus}
              exclusive
              onChange={(_, value) => {
                setTrackerControlStatus?.(value)
                setPaginationModel?.(prev => ({ ...prev, page: 0 }))
              }}
            >
              <TabSegmented value={TrackerControlStatus.UNSPECIFIED}>
                <Box display="flex" gap={0.5} alignItems="center">
                  <Typography variant="label">Overall</Typography>
                </Box>
              </TabSegmented>
              <TabSegmented value={TrackerControlStatus.CONTROLLED}>
                <Box display="flex" gap={0.5} alignItems="center">
                  <Typography variant="label">Controlled</Typography>
                </Box>
              </TabSegmented>
              <TabSegmented value={TrackerControlStatus.UNCONTROLLED}>
                <Box display="flex" gap={0.5} alignItems="center">
                  <Typography variant="label">Uncontrolled</Typography>
                </Box>
              </TabSegmented>
            </TabSegmentedGroup>
          ) : null}

          <TabSegmentedGroup
            size="small"
            value={trackerAuthorizationStatus}
            exclusive
            onChange={(_, value) => {
              setTrackerAuthorizationStatus?.(value)
              setPaginationModel?.(prev => ({ ...prev, page: 0 }))
            }}
          >
            <TabSegmented value={TrackerAuthorizationStatus.AUTHORIZED}>
              <Box display="flex" gap={0.5} alignItems="center">
                <Typography variant="label">Authorized</Typography>
              </Box>
            </TabSegmented>
            <TabSegmented value={TrackerAuthorizationStatus.UNAUTHORIZED}>
              <Box display="flex" gap={0.5} alignItems="center">
                <Typography variant="label">Unauthorized</Typography>
              </Box>
            </TabSegmented>
          </TabSegmentedGroup>
        </Box>
      ) : null}
    </Box>
  )
}
